import React, { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { API } from 'utilities/api';
import { T } from './authStyles';
import SignIn from './SignIn';
import SignUp from './SignUp';

/**
 * Auth modal — the only sign-in / sign-up surface in the app.
 *
 * Owns three concerns:
 *   1. Modal lifecycle (open/close, escape key, body scroll lock).
 *   2. Tab switching between SignIn and SignUp.
 *   3. The post-auth redirect (`handleSuccess`), which is the single
 *      decision point for "where does this user go after logging in?".
 *
 * `notice` renders an explanatory banner above the tabs — used to surface
 * why a social sign-in bounced the user back here.
 *
 * Sub-components (SignIn / SignUp / OAuthButtons / Divider) are
 * deliberately in their own files — one component per file, so each
 * flow can be modified or debugged in isolation.
 */
export default function AuthModal({ isOpen, onClose, defaultTab = 0, notice = null }) {
  const [tab, setTab] = useState(defaultTab);
  const { token: ctxToken, login } = useAuth();

  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSuccess = (userPayload) => {
    onClose();
    // If a flow (e.g. accept-invite) stashed a return path, prefer that
    // over the default landing. Pop it so the next auth doesn't reuse
    // a stale destination.
    let stashed = null;
    try {
      stashed = sessionStorage.getItem('tpo_post_auth_redirect');
      if (stashed) sessionStorage.removeItem('tpo_post_auth_redirect');
    } catch (_) { /* private mode */ }
    if (stashed) {
      window.location.href = stashed;
      return;
    }
    // Prefer the admin flag the verify endpoint just handed us. Fall
    // back to getUserProfile only if the caller didn't pass a payload
    // (e.g. OAuth flow that doesn't go through this modal). The
    // fallback also writes the fetched profile into AuthContext so the
    // destination layout doesn't sit on a spinner waiting for a user
    // that nobody cached.
    const route = (profile) => {
      const target = profile?.is_platform_admin === true
        ? '/admin'
        : '/dashboard/brand/overview';
      window.location.href = target;
    };
    if (userPayload && typeof userPayload === 'object') {
      route(userPayload);
      return;
    }
    API.getUserProfile()
      .catch(() => null)
      .then((profile) => {
        if (profile && typeof profile === 'object' && ctxToken) {
          login(ctxToken, profile);
        }
        route(profile);
      });
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
      `}</style>
      <div style={T.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={T.card}>
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
          >
            <MdClose size={20} />
          </button>

          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 26, fontWeight: 800, background: 'linear-gradient(to right, #A78BFA, #60A5FA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' }}>
              TechProOne
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 4 }}>Your brand. Your domain. Your rules.</div>
          </div>

          {notice && (
            <div style={{
              background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10, padding: '10px 14px', color: '#FCA5A5', fontSize: 13,
              marginBottom: 16, textAlign: 'center',
            }}>
              {notice}
            </div>
          )}

          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: 4, marginBottom: 24, gap: 4 }}>
            {['Sign In', 'Sign Up'].map((label, i) => (
              <button
                key={label}
                onClick={() => setTab(i)}
                style={{
                  flex: 1, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 14,
                  background: tab === i ? 'rgba(139,92,246,0.35)' : 'transparent',
                  color: tab === i ? '#fff' : 'rgba(255,255,255,0.45)',
                  boxShadow: tab === i ? '0 2px 8px rgba(139,92,246,0.3)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 0 ? <SignIn onSuccess={handleSuccess} /> : <SignUp onSuccess={handleSuccess} />}
        </div>
      </div>
    </>
  );
}
